import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth, writeDevSession } from '../lib/auth';

export default function AuthVerify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const token = searchParams.get('token') || '';
    const next = searchParams.get('next') || '/my-courses';

    if (!token) {
      navigate('/login?auth=invalid', { replace: true });
      return;
    }

    async function verify() {
      try {
        const res = await fetch(
          `/api/auth/verify?token=${encodeURIComponent(token)}&next=${encodeURIComponent(next)}`,
          { credentials: 'include', cache: 'no-store' }
        );
        const json = await res.json().catch(() => ({}));

        if (!res.ok) {
          navigate(json.redirect || '/login?auth=expired', { replace: true });
          return;
        }

        // In dev the cookie may be stripped by the Replit proxy — persist the
        // session ID in localStorage so the Bearer header fallback kicks in.
        if (json.sessionId) writeDevSession(json.sessionId);

        await refresh();
        navigate(json.next || next, { replace: true });
      } catch {
        setError('Unable to reach the server. Please check your connection and try again.');
      }
    }

    verify();
  }, [searchParams, navigate, refresh]);

  if (error) {
    return (
      <div className="w-full bg-bone min-h-screen flex flex-col items-center justify-center">
        <div className="max-w-md text-center px-6">
          <div className="text-[11px] tracking-[0.28em] uppercase text-brand-aubergine mb-4">
            Learning Outcomes Academy
          </div>
          <h1 className="font-display text-3xl text-ink mb-4">Something went wrong</h1>
          <p className="text-slate2 text-base leading-relaxed mb-6">{error}</p>
          <a href="/login" className="btn-primary inline-flex">
            Back to sign in
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-bone min-h-screen flex flex-col items-center justify-center">
      <div className="max-w-md text-center px-6">
        <div className="text-[11px] tracking-[0.28em] uppercase text-brand-aubergine mb-6">
          Learning Outcomes Academy
        </div>
        <div className="flex justify-center mb-6">
          <div className="w-8 h-8 border-2 border-brand-deep border-t-transparent rounded-full animate-spin" />
        </div>
        <p className="text-slate2 text-base">Signing you in…</p>
      </div>
    </div>
  );
}
